import {
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Dialog,
  Divider,
  Stack,
  Typography,
  useMediaQuery
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { ChevronDown, X } from "lucide-react";
import {
  useEffect,
  useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ModifyContentModalBox
} from "./ModifyContentModal.styles";
import type { ModifyContentModalProps } from "./ModifyContentModal.types";

import {
  Icon ,
  CreateModalSuccess,
  Spinner
} from "../../components";
import { ButtonDigited } from "../../components/Button/Button.variants";
import {
  BodyInputBase,
  ContentBox,
  ForbiddenLabelTypography,
  LinkAccordion,
  StyledError,
  TextContent,
  TitleInputBase,
  TitleTypography,
  UserInfoBox,
  UserNameTypography
} from "../../components/CreateContentModal/CreateContentModal.styles";

import { POST_TYPE } from "../../consts";
import { useGetProfileImageQuery } from "../../queries";
import {
  useModifyPostQuery
} from "../../queries/community";
import { useAuthStore } from "../../stores";
import { stringAvatar } from "../../utils/community";

export function ModifyContentModal({
  body,
  closeDrawer,
  id,
  onClose,
  open,
  postType,
  selectedTopic,
  selectedFilter,
  ...props
}: ModifyContentModalProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const userData = useAuthStore(state => state.userData);
  const [bodyValue, setBodyValue] = useState<string | undefined>(body);
  const [linkValue, setLinkValue] = useState<string | undefined>("linkvalue" in props ? props.linkvalue : undefined);
  const [linkLabel, setLinkLabel] = useState<string | undefined>("linklabel" in props ? props.linklabel : undefined);
  const [showLinkForbiddenLabel, setShowLinkForbiddenLabel]= useState(false);
  const [showBodyForbiddenLabel, setShowBodyForbiddenLabel]= useState(false);
  const [setUrl, setSetUrl]= useState(Boolean(linkValue));

  const { data: profileImage  } = useGetProfileImageQuery({});

  const {
    mutate: mutateModify,
    isPending: modifyIsLoading,
    isError,
    isSuccess,
    error
  } = useModifyPostQuery(
    postType === POST_TYPE.FREE ? {
      description: bodyValue,
      id: id,
      linkLabel: linkLabel,
      linkValue: linkValue,
      postType: postType,
      selectedFilter:selectedFilter,
      selectedTopic:selectedTopic
    } : {
      description: bodyValue,
      id,
      loId: "loId" in props ? props.loId : "",
      postType,
      selectedFilter:selectedFilter,
      selectedTopic:selectedTopic
    });

  //managing forbidden word
  useEffect(() => {
    if( !modifyIsLoading && isError) {
      const errorObject = JSON.parse(error as unknown as string);

      if(errorObject.message == "Forbidden word has been detected.") {
        errorObject.errors.forEach((error: Record<string, string|Array<string>>) => {
          let errorBody = "" as string;

          switch (error.field) {
          case "body":
            errorBody = bodyValue ? replaceForbidden(bodyValue, error.words as string[]) : "";
            setBodyValue(errorBody);
            setShowBodyForbiddenLabel(true);
            break;

          case "attachments_link":
            setShowLinkForbiddenLabel(true);
            break;
          default:
            break;
          }
        });
      }
    }
  },[
    bodyValue,
    error,
    isError,
    modifyIsLoading
  ]);

  return (
    (<Dialog
      maxWidth={ false }
      open={ open }
      onClose={ onClose }
      scroll={ "paper" }
      sx={ {
        ".MuiDialog-paper": {
          background: theme.customColors.backgroundCard,
          border: `2px solid ${theme.customColors.border}`,
          borderRadius: "1.125rem",
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          minHeight: "fit-content !important",
          minWidth: "32rem",
          transition: "all 0.2s ease",
          [theme.breakpoints.down("sm")]: {
            minWidth: "calc(100% - 2rem)"
          }
        }
      } }
    >
      <ModifyContentModalBox
        container
        { ...props }
      >
        {
          modifyIsLoading ?

            <Stack
              alignItems="center"
              justifyContent="center"
              width="100%"
            >
              <Spinner/>
            </Stack> :

            isSuccess ?
              <CreateModalSuccess
                title={ t("modified_post") }
                onClose={ () => closeDrawer() }
              /> :

              <>
                {
                  isError ?
                    <StyledError>
                      <Icon
                        color={ theme.customColors.textWarning }
                        icon="Icons_obbligatorio"
                        size={ 24 }
                      />
                      { t("forbidden_label_topic") }
                    </StyledError> :
                    null
                }
                <Box
                  sx={ {
                    position: "absolute",
                    right: "1.25rem"
                  } }
                >
                  <X
                    aria-label={ t("close") }
                    role="button"
                    tabIndex={ 0 }
                    size={ "1.5rem" }
                    cursor="pointer"
                    color={ theme.customColors?.exploreCardText }
                    onClick={ () => closeDrawer() }
                    onKeyDown={ (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        closeDrawer();
                      }
                    } }
                  />
                </Box>
                <Box
                  display={ "flex" }
                  flexDirection={ "column" }
                  gap={ "24px" }
                  width="100%"
                >
                  <TextContent>
                    <TitleTypography>
                      { t("edit_post") }
                    </TitleTypography>
                  </TextContent>

                  {
                    postType === POST_TYPE.FREE ?
                      <UserInfoBox aria-hidden="true">
                        <Avatar
                          alt={ `${userData?.name} ${userData?.surname} ` }
                          { ...stringAvatar(`${userData?.name} ${userData?.surname} `) }
                          sx={ { height: "40px", width: "40px", fontSize: "0.8rem" } }
                          src={ profileImage }
                        />
                        <UserNameTypography>
                          { userData?.name }  { userData?.surname }
                        </UserNameTypography>
                      </UserInfoBox> :
                      null
                  }

                  <Stack gap={ "16px" }>
                    { showBodyForbiddenLabel &&
                      <ForbiddenLabelTypography key={ "forbidden-label-body" }>
                        { t("forbidden_label_topic") }
                      </ForbiddenLabelTypography>
                    }
                    <BodyInputBase
                      inputProps={ { "aria-label": t("talk_about") } }
                      multiline
                      value={ bodyValue }
                      onChange={ (changeEvent) => setBodyValue(changeEvent.target.value) }
                      placeholder={ `${t("talk_about")}` }
                    />
                  </Stack>

                  {
                    postType === POST_TYPE.FREE ?
                      <ContentBox>
                        <LinkAccordion
                          expanded={ setUrl }
                          onChange={ (_, expanded) => setSetUrl(expanded) }
                          disableGutters
                        >
                          <AccordionSummary
                            expandIcon={ <ChevronDown size={ 16 } color={ theme.customColors.textPrimary } /> }
                          >
                            <Box display="flex" alignItems="center" gap={ 1 }>
                              <Icon
                                color={ theme.customColors.textPrimary }
                                icon={ "Icons_link" }
                                size={ isMobile ? "15px" : "20px" }
                              />
                              <Typography
                                color={ theme.customColors.textPrimary }
                                fontSize="16px"
                              >
                                { "Link" }
                              </Typography>
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails>
                            { showLinkForbiddenLabel &&
                              <ForbiddenLabelTypography key={ "forbidden-label-link" }>
                                { t("forbidden_text") }
                              </ForbiddenLabelTypography>
                            }
                            <Stack
                              flexDirection={ "column" }
                              gap={ theme.spacing(2) }
                              width="100%"
                            >
                              <Divider/>
                              <TitleInputBase
                                value={ linkValue }
                                onChange={ (changeEvent) => {
                                  setShowLinkForbiddenLabel(false);
                                  setLinkValue(changeEvent.target.value);
                                } }
                                placeholder={ `${t("insert_url")}` }
                                sx={ {
                                  [theme.breakpoints.down("sm")]:{
                                    width: "252px"
                                  }
                                } }
                              />
                              <TitleInputBase
                                value={ linkLabel }
                                onChange={ (changeEvent) => {
                                  setShowLinkForbiddenLabel(false);
                                  const uri = changeEvent.target.value.replace(/.+\/\/|www.|\.+/g, "");
                                  const encoded = encodeURI(uri);
                                  const decoded = decodeURI(encoded);
                                  setLinkLabel(decoded);
                                } }
                                placeholder={ `${t("insert_link_title")}` }
                                sx={ {
                                  [theme.breakpoints.down("sm")]:{
                                    width: "252px"
                                  }
                                } }
                              />
                            </Stack>
                          </AccordionDetails>
                        </LinkAccordion>
                        <Stack
                          flexDirection="row"
                          justifyContent={ "center" }
                          marginTop={ "16px" }
                        >
                          <ButtonDigited
                            typology="primary"
                            sx={ {
                              gap:theme.spacing(3),
                              maxWidth:"120px"
                            } }
                            disabled={ !bodyValue }
                            onClick={ () => mutateModify() }
                          >
                            { t("modify") }
                          </ButtonDigited>
                        </Stack>
                      </ContentBox> :

                      <Stack
                        flexDirection="row"
                        justifyContent={ "center" }
                        marginTop={ "16px" }
                      >
                        <ButtonDigited
                          typology="primary"
                          sx={ {
                            gap:theme.spacing(3),
                            maxWidth:"120px"
                          } }
                          disabled={ !bodyValue }
                          onClick={ () => mutateModify() }
                        >
                          { t("modify") }
                        </ButtonDigited>
                      </Stack>
                  }
                </Box>
              </>
        }
      </ModifyContentModalBox>
    </Dialog>)
  );

  function replaceForbidden(html: string, forbiddenWords: Array<string>): string {
    return forbiddenWords.reduce(
      (partialHtml, currentWord) => {
        const reg = new RegExp(currentWord, "gmi");
        return partialHtml.replace(reg, `<span style="color: red;"><ins><strong>${currentWord}</strong></ins></span>`);
      },
      html
    );
  }

}
